import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Loader2, Flame, LayoutGrid, List, Sparkles, ClipboardList, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import PlanningGrid from '../components/PlanningGrid';
import MesCreneaux from '../components/MesCreneaux';
import { useAuth } from '../contexts/AuthContext';

const DAYS = ['Lundi', 'Mardi', 'Jeudi', 'Vendredi', 'Samedi'];

export default function Planning() {
  const { isAuthenticated, user } = useAuth();
  const [slots, setSlots] = useState(null);
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [known, setKnown] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => axios.get(`${API}/planning`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  useEffect(() => { load(); }, []);

  const [banner, setBanner] = useState(null);
  useEffect(() => {
    axios.get(`${API}/content/public`).then((r) => setBanner(r.data.discovery_banner)).catch(() => {});
  }, []);

  const lookup = async (email) => {
    if (!email || !email.includes('@')) return;
    try {
      const r = await axios.get(`${API}/planning/lookup`, { params: { email } });
      if (r.data.found) {
        setKnown(true);
        setForm((f) => ({ ...f, first_name: r.data.first_name || f.first_name, last_name: r.data.last_name || f.last_name, phone: r.data.phone || f.phone }));
        toast.success('Compte reconnu — vos informations ont été pré-remplies.');
      }
    } catch (e) { /* silencieux */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/planning/prebook`, { slot_id: selected.id, ...form, phone: form.phone || null });
      if (r.data.status === 'preblocked') {
        toast.success('Votre place est pré-bloquée ! Nous vous recontacterons avant l\'ouverture.');
      } else {
        toast.info(`Séance complète — vous êtes en liste d'attente (position ${r.data.position}).`);
      }
      setSelected(null);
      setForm({ first_name: '', last_name: '', email: '', phone: '' });
      setKnown(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!slots) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F5EFE5]"><Loader2 className="w-6 h-6 animate-spin text-[#C99A55]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F5EFE5] pb-20" data-testid="planning-page">
      <div className="max-w-6xl mx-auto px-4 pt-10">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#C99A55] font-semibold mb-2">Planning prévisionnel – Ouverture PURO</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#493329] mb-3">Le planning PURO</h1>
        <p className="text-[#7A6455] max-w-2xl text-sm sm:text-base mb-2">
          Les réservations sont actuellement ouvertes en pré-réservation. Pré-bloquez votre place dès maintenant avant l'ouverture officielle du studio.
        </p>
        <p className="text-[#9A744C] text-sm mb-6">Chaque séance est limitée à <strong>6 participantes maximum</strong>.</p>

        {banner?.enabled && (
          <div className="mb-6 rounded-2xl border border-[#C99A55]/40 bg-gradient-to-r from-[#FBF6EC] to-[#F3E8D4] p-5 flex items-start gap-4" data-testid="discovery-banner">
            <div className="w-10 h-10 rounded-full bg-[#C99A55]/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-[#C99A55]" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-[#493329]" data-testid="discovery-banner-title">{banner.title}</h3>
              <p className="text-sm text-[#7A6455] mt-1" data-testid="discovery-banner-message">{banner.message}</p>
              {(banner.start_date || banner.end_date) && (
                <p className="text-xs text-[#9A744C] mt-2">
                  {banner.start_date && <>Du {new Date(banner.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} </>}
                  {banner.end_date && <>au {new Date(banner.end_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</>}
                </p>
              )}
            </div>
          </div>
        )}

        {isAuthenticated && user?.role === 'client' && (
          <Link to="/sondage" className="mb-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#493329] text-white text-sm hover:bg-[#5c4335] transition-colors" data-testid="planning-survey-cta">
            <ClipboardList className="w-4 h-4" /> Donnez votre avis sur les créneaux
          </Link>
        )}

        <Link to="/inauguration" className="mb-6 ml-0 sm:ml-3 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C99A55] text-white text-sm hover:bg-[#b5873f] transition-colors shadow-sm" data-testid="planning-event-cta">
          <PartyPopper className="w-4 h-4" /> Inauguration : réservez votre place
        </Link>

        {isAuthenticated && user?.role === 'client' && <div className="mt-6"><MesCreneaux /></div>}

        <div className="flex items-center gap-2 mb-6">
          {[['grid', 'Vue grille', LayoutGrid], ['list', 'Vue liste', List]].map(([v, label, Icon]) => (
            <button key={v} onClick={() => setView(v)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs transition-colors ${view === v ? 'bg-[#493329] text-white' : 'bg-white border border-[#E9DDCC] text-[#7A6455]'}`}
              data-testid={`planning-view-${v}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {view === 'grid' && <PlanningGrid slots={slots} onSelect={setSelected} testPrefix="client" />}

        {view === 'list' && (
        <div className="space-y-10">
          {DAYS.map((day) => (
            <div key={day}>
              <h2 className="font-heading text-lg text-[#493329] mb-3 flex items-center gap-3">
                {day}
                <span className="flex-1 h-px bg-[#E9DDCC]" />
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {slots.filter((s) => s.day === day).map((s) => (
                  <div key={s.id} className={`bg-white rounded-2xl border p-5 flex flex-col gap-2 ${s.highlight ? 'border-[#C99A55]' : 'border-[#E9DDCC]'}`} data-testid={`slot-${s.id}`}>
                    {s.highlight && (
                      <span className="inline-flex items-center gap-1 self-start px-2.5 py-1 rounded-full bg-[#493329] text-[#C99A55] text-[10px] uppercase tracking-wider">
                        <Flame className="w-3 h-3" /> {s.highlight}
                      </span>
                    )}
                    <div className="font-heading text-[#493329]">{s.type}</div>
                    <div className="text-sm text-[#7A6455]">{s.day} · {s.start} – {s.end}</div>
                    {s.full ? (
                      <span className="text-xs font-semibold text-[#C76542] uppercase tracking-wider" data-testid={`slot-full-${s.id}`}>Complet ({s.reserved}/{s.capacity}){s.waitlist_count > 0 ? ` · ${s.waitlist_count} en attente` : ''}</span>
                    ) : (
                      <span className={`text-xs ${s.remaining === 1 ? 'text-[#C76542] font-semibold' : 'text-[#7A6455]'}`} data-testid={`slot-remaining-${s.id}`}>
                        {s.remaining} place{s.remaining > 1 ? 's' : ''} restante{s.remaining > 1 ? 's' : ''} sur {s.capacity}
                      </span>
                    )}
                    <Button
                      onClick={() => setSelected(s)}
                      className={`mt-1 rounded-full h-10 text-sm ${s.full ? 'bg-[#493329] hover:bg-[#C99A55]' : 'bg-[#C99A55] hover:bg-[#493329]'} text-white`}
                      data-testid={`prebook-btn-${s.id}`}
                    >
                      {s.full ? "Rejoindre la liste d'attente" : 'Pré-bloquer ma place'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="prebook-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-normal text-[#493329]">
              {selected?.full ? "Rejoindre la liste d'attente" : 'Pré-bloquer ma place'}
            </DialogTitle>
            <DialogDescription className="sr-only">Formulaire de pré-réservation d'une séance PURO</DialogDescription>
          </DialogHeader>
          {selected && (
            <form onSubmit={submit} className="space-y-3">
              <div className="bg-[#F5EFE5] rounded-xl p-3 text-sm text-[#493329]">
                <strong>{selected.type}</strong><br />{selected.day} · {selected.start} – {selected.end}
              </div>
              <Input type="email" required placeholder="Votre email *" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                onBlur={(e) => lookup(e.target.value)} data-testid="prebook-email" className="h-11 rounded-xl" />
              {known && <p className="text-xs text-[#7C9C82]">✓ Compte PURO reconnu</p>}
              <div className="grid grid-cols-2 gap-2">
                <Input required placeholder="Prénom *" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="prebook-firstname" className="h-11 rounded-xl" />
                <Input placeholder="Nom" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="prebook-lastname" className="h-11 rounded-xl" />
              </div>
              <Input placeholder="Téléphone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="prebook-phone" className="h-11 rounded-xl" />
              <Button type="submit" disabled={submitting} className="w-full bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11" data-testid="prebook-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (selected.full ? "Rejoindre la liste d'attente" : 'Confirmer ma pré-réservation')}
              </Button>
              <p className="text-[11px] text-[#AE9885] text-center">Aucun paiement requis — votre place sera confirmée avant l'ouverture.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
